import type { Ref } from 'react';
import React, { useImperativeHandle, useState } from 'react';

/**
 * Honeypot form state type.
 */
type FormState = {
  firstName: string;
  lastName: string;
  email: string;
};

/**
 * Type of the ref of a {@link SecurityForm}.
 * The ref only contains the state of the form, so this type is implicitly also the form state.
 */
export type SecurityFormRef = FormState;

/**
 * Properties of a {@link SecurityForm} component.
 */
export type SecurityFormProps = {
  onChange?: (state: FormState) => void;
};

/**
 * A Honeypot form, which is meant to help with bot protection.
 *
 * @return the created {@link React.JSX.Element}.
 */
export const SecurityForm = function ({
  onChange,
  ref
}: Readonly<SecurityFormProps> & {
  ref?: Ref<SecurityFormRef>;
}): React.JSX.Element {
  const [state, setState] = useState<FormState>({
    firstName: '',
    lastName: '',
    email: ''
  });

  const stateChange = (changes: Partial<FormState>) => {
    const newState = { ...state, ...changes };
    setState(newState);
    onChange?.(newState);
  };

  // imperative handle to put the form state into the forwarded ref. To make accessing the state directly without using `onChange` possible.
  useImperativeHandle<Record<string, unknown>, FormState>(ref, () => state, [
    state
  ]);

  return (
    <div aria-hidden={true} className="ehs-security-form">
      <input
        className="security-form-form-element"
        type="text"
        name="firstName"
        tabIndex={-1}
        autoComplete="off"
        value={state.firstName ?? ''}
        onChange={event => {
          stateChange({
            firstName: event.target.value
          });
        }}
      />
      <input
        className="security-form-form-element"
        type="text"
        name="lastName"
        tabIndex={-1}
        autoComplete="off"
        value={state.lastName ?? ''}
        onChange={event => {
          stateChange({
            lastName: event.target.value
          });
        }}
      />
      <input
        className="security-form-form-element"
        type="text"
        name="email"
        tabIndex={-1}
        autoComplete="off"
        value={state.email ?? ''}
        onChange={event => {
          stateChange({
            email: event.target.value
          });
        }}
      />
    </div>
  );
};
